import { c as _c } from "react/compiler-runtime";
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { Button, Close, Discount, SpreadIcon } from '@sprd/sprd-component-kit/v2';
import { usePortalClosing } from '@sprd/spreadshirt-shell';
import { useEffect } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { useLocale, useTranslations } from 'next-intl';
import { Toaster } from 'sonner';
import { Drawer } from 'vaul';
import { useCart, useShippingMethods } from '@/hooks/useCartHooks';
import { useIsFreeShippingBannerEnabled } from '@/hooks/useIsFreeShippingBannerEnabled';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { usePageChrome } from '@/hooks/usePageChrome';
import { basketAtom, expressCheckoutLoadingAtom } from '@/store/atoms';
import { getFreeShippingBannerRemainder } from '@/utility/getFreeShippingBannerVisibility';
import { getAssetUrlByName } from '@/utility/getImageUrl';
import { Locale, getLanguage } from '@/utility/locale';
import { tracker } from '@/utility/tracking/tracker';
import NestedPromoBanner from '../HeaderBanner/NestedPromoBanner';
import { BasketItem } from './BasketItem';
import { DesktopBasketPrices, MobileBasketPrices, getBasketSubtotal } from './BasketPrices';
const volumeDiscountLang = ['fr-CA', 'en-GB', 'en-US'];
const getVolumeDiscountPageUrl = (locale: Locale) => {
  const language = (volumeDiscountLang.includes(locale) ? locale.toLowerCase() : getLanguage(locale)) ?? 'en-gb';
  return `https://help.spreadshirt.com/hc/${language}/articles/207863285`;
};
export const MiniBasket = t0 => {
  const $ = _c(53);
  const {
    amplienceMediaBaseUrl
  } = t0;
  const {
    hidePromoBanner
  } = usePageChrome();
  const t = useTranslations();
  const locale = useLocale();
  const {
    data: cartAndLineItemGroups,
    isPending,
    isFetching
  } = useCart();
  const {
    data: shippingMethodsResponse
  } = useShippingMethods(cartAndLineItemGroups?.cart.id, cartAndLineItemGroups?.cart.country);
  const isTablet = useMediaQuery("(min-width: 768px)");
  const [isOpenBasket, setOpenBasket] = useAtom(basketAtom);
  const isExpressCheckoutLoading = useAtomValue(expressCheckoutLoadingAtom);
  let t1;
  let t2;
  if ($[0] !== isOpenBasket) {
    t1 = () => {
      if (isOpenBasket) {
        tracker.trackFeature({
          featureUsed: "cart-view",
          detail: "minibasket"
        });
      }
    };
    t2 = [isOpenBasket];
    $[0] = isOpenBasket;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  const {
    willOpen,
    handleOpenChange
  } = usePortalClosing(isOpenBasket, setOpenBasket);
  const hasCartItems = cartAndLineItemGroups?.cart?.lineItems && cartAndLineItemGroups?.cart?.lineItems?.length > 0;
  const isLoading = isPending || isFetching || isExpressCheckoutLoading;
  const isFreeShippingBannerEnabled = useIsFreeShippingBannerEnabled();
  let t3;
  if ($[3] !== cartAndLineItemGroups || $[4] !== hasCartItems || $[5] !== isFreeShippingBannerEnabled || $[6] !== shippingMethodsResponse) {
    t3 = isFreeShippingBannerEnabled && hasCartItems && cartAndLineItemGroups ? getFreeShippingBannerRemainder(shippingMethodsResponse, getBasketSubtotal(cartAndLineItemGroups.cart)) : undefined;
    $[3] = cartAndLineItemGroups;
    $[4] = hasCartItems;
    $[5] = isFreeShippingBannerEnabled;
    $[6] = shippingMethodsResponse;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const freeShippingBannerRemainderCents = t3;
  const t4 = isTablet ? "right" : "bottom";
  const t5 = !isExpressCheckoutLoading;
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Drawer.Overlay className="force-animation-150 absolute inset-0 z-50 bg-black-alpha-400" />;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      top: "3.75rem"
    };
    t8 = {
      top: "3.75rem",
      left: 0,
      right: 0
    };
    $[9] = t7;
    $[10] = t8;
  } else {
    t7 = $[9];
    t8 = $[10];
  }
  let t9;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <Toaster id="minibasket" position="top-center" offset={t7} mobileOffset={t8} toastOptions={{
      unstyled: true,
      style: {
        width: "100%",
        maxWidth: "none",
        padding: 0,
        background: "transparent",
        border: "none",
        boxShadow: "none"
      },
      classNames: {
        toast: "!box-border !w-full !max-w-none !p-0 !px-4 sm:!px-8 !bg-transparent !shadow-none !border-0"
      }
    }} className="pointer-events-none z-[200] !w-full !max-w-none" />;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== t) {
    t10 = t("miniBasket.headline");
    $[12] = t;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] !== t10) {
    t11 = <VisuallyHidden><Drawer.Title>{t10}</Drawer.Title></VisuallyHidden>;
    $[14] = t10;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== t) {
    t12 = t("miniBasket.headline");
    $[16] = t;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== t12) {
    t13 = <h2 className="text-base">{t12}</h2>;
    $[18] = t12;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  let t14;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <Close className="size-6" />;
    $[20] = t14;
  } else {
    t14 = $[20];
  }
  let t15;
  if ($[21] !== t) {
    t15 = t("general.close");
    $[21] = t;
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  let t16;
  if ($[23] !== t15) {
    t16 = <Drawer.Close asChild={true}><Button variant="plain" className="p-0!">{t14}<span className="sr-only">{t15}</span></Button></Drawer.Close>;
    $[23] = t15;
    $[24] = t16;
  } else {
    t16 = $[24];
  }
  let t17;
  if ($[25] !== t13 || $[26] !== t16) {
    t17 = <div className="flex items-center justify-between px-4 py-4 sm:px-8">{t13}{t16}</div>;
    $[25] = t13;
    $[26] = t16;
    $[27] = t17;
  } else {
    t17 = $[27];
  }
  let t18;
  if ($[28] !== amplienceMediaBaseUrl || $[29] !== cartAndLineItemGroups || $[30] !== freeShippingBannerRemainderCents || $[31] !== hasCartItems || $[32] !== hidePromoBanner || $[33] !== locale || $[34] !== t) {
    t18 = hasCartItems ? <>{!hidePromoBanner && <div className="pb-3 sm:hidden"><NestedPromoBanner /></div>}<div className="flex flex-col items-center justify-center px-4 sm:px-8"><p className="flex items-center justify-center gap-3 text-sm sm:gap-2.5"><Discount className="inline-block size-4" /><span>{t.rich("miniBasket.discountHint", {
              link: chunks => <a href={getVolumeDiscountPageUrl(locale)} target="_blank" rel="noopener noreferrer" className="underline">{chunks}</a>
            })}</span></p><div className="mt-3 h-px w-full bg-neutral-200" /></div><div className="grow overflow-y-auto px-4 sm:px-8"><ul className="my-6 space-y-6">{cartAndLineItemGroups.lineItemGroups.map(_temp)}</ul><MobileBasketPrices cart={cartAndLineItemGroups.cart} /></div><DesktopBasketPrices cartAndLineItemGroups={cartAndLineItemGroups} locale={locale} freeShippingBannerRemainderCents={freeShippingBannerRemainderCents} /></> : <div className="flex h-full flex-col items-center justify-center px-16"><img src={getAssetUrlByName("minibasket_placeholder", amplienceMediaBaseUrl, 450)} alt="Empty basket" className="mb-6 w-full object-contain" /><h3 className="text-lg">{t("miniBasket.headlineEmpty")}</h3><p className="px-4 pt-2 text-center">{t("miniBasket.sublineEmpty")}</p><Drawer.Close asChild={true}><Button variant="primary" size="l" className="mt-6">{t("miniBasket.ctaEmpty")}</Button></Drawer.Close></div>;
    $[28] = amplienceMediaBaseUrl;
    $[29] = cartAndLineItemGroups;
    $[30] = freeShippingBannerRemainderCents;
    $[31] = hasCartItems;
    $[32] = hidePromoBanner;
    $[33] = locale;
    $[34] = t;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  let t19;
  if ($[36] !== t17 || $[37] !== t18) {
    t19 = <div className="flex min-h-full grow flex-col md:mt-0">{t17}{t18}</div>;
    $[36] = t17;
    $[37] = t18;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  let t20;
  if ($[39] !== isLoading || $[40] !== t) {
    t20 = isLoading && <div aria-label={t("general.a11y.loading")}><div className="absolute inset-0 z-10 h-full w-full bg-white-alpha-400" /><SpreadIcon className="animate-scale absolute top-[45%] left-1/2 z-11 size-26 -translate-1/2" /></div>;
    $[39] = isLoading;
    $[40] = t;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  let t21;
  if ($[42] !== isLoading || $[43] !== t11 || $[44] !== t19 || $[45] !== t20) {
    t21 = <Drawer.Portal>{t6}<Drawer.Content aria-busy={isLoading} className="force-animation-150 fixed right-0 bottom-0 z-100 flex h-[calc(100dvh-1rem)] w-full flex-col overflow-hidden bg-white max-sm:rounded-t-xl sm:h-dvh sm:w-140">{t9}{t11}{t19}{t20}</Drawer.Content></Drawer.Portal>;
    $[42] = isLoading;
    $[43] = t11;
    $[44] = t19;
    $[45] = t20;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  let t22;
  if ($[47] !== handleOpenChange || $[48] !== t21 || $[49] !== t4 || $[50] !== t5 || $[51] !== willOpen) {
    t22 = <Drawer.Root open={willOpen} onOpenChange={handleOpenChange} direction={t4} dismissible={t5} autoFocus={true}>{t21}</Drawer.Root>;
    $[47] = handleOpenChange;
    $[48] = t21;
    $[49] = t4;
    $[50] = t5;
    $[51] = willOpen;
    $[52] = t22;
  } else {
    t22 = $[52];
  }
  return t22;
};
function _temp(group) {
  return <BasketItem lineItemGroup={group} key={`${group.groupId}-${group.lineItems.length}`} />;
}